import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { getCreditStatus, getPlanDisplayName, getPlanColor } from '../services/api';
import { Gem, TrendingUp, Calendar, Loader, ShoppingCart } from 'lucide-react';
import './CreditStatus.css';

const CreditStatus = forwardRef(({ compact = false, onUpgrade }, ref) => {
  const [creditStatus, setCreditStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCreditStatus();
  }, []);

  const loadCreditStatus = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const status = await getCreditStatus();
      setCreditStatus(status);
      setError(null);
    } catch (err) {
      console.error('Error loading credit status:', err);
      setError('Failed to load credit status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: () => loadCreditStatus(true)
  }));

  const getResetMessage = () => {
    if (!creditStatus?.credits_reset_date) return '';
    
    const resetDate = new Date(creditStatus.credits_reset_date);
    const now = new Date();
    const diffHours = Math.floor((resetDate - now) / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return 'Resets in less than 1 hour';
    if (diffHours < 24) return `Resets in ${diffHours} hours`;
    if (diffDays === 1) return 'Resets tomorrow';
    return `Resets in ${diffDays} days`;
  };

  if (loading && !creditStatus) {
    return (
      <div className={`credit-status ${compact ? 'compact' : ''}`}>
        <div className="credit-loading">
          <div className="spinner-small"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !creditStatus) {
    return (
      <div className={`credit-status ${compact ? 'compact' : ''} error`}>
        <p>Unable to load credits</p>
        <button onClick={() => loadCreditStatus(false)} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  // Compact version for header/sidebar
  if (compact) {
    return (
      <div className="credit-status compact">
        <div className="credit-compact-display">
          <Gem size={20} className="credit-gem-icon" />
          <div className="credit-compact-info">
            <span className="credit-amount">{creditStatus.total_credits}</span>
            <span className="credit-label">credits</span>
          </div>
          
          {/* Inline loading spinner */}
          {refreshing && (
            <Loader size={16} className="credit-refresh-spinner" />
          )}
          
          {!creditStatus.can_generate_video && !refreshing && (
            <button 
              onClick={onUpgrade} 
              className="btn-upgrade-compact"
            >
              <span className="btn-upgrade-text">Get More</span>
              <ShoppingCart size={18} className="btn-upgrade-icon" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Full version for dashboard
  return (
    <div className="credit-status">
      <div className="credit-header">
        <div className="credit-header-left">
          <h3>Your Credits</h3>
          {refreshing && (
            <Loader size={18} className="credit-refresh-spinner" />
          )}
        </div>
        <span 
          className="plan-badge"
          style={{ backgroundColor: getPlanColor(creditStatus.subscription_plan) }}
        >
          {getPlanDisplayName(creditStatus.subscription_plan)}
        </span>
      </div>

      {/* Total Credits */}
      <div className="credit-total">
        <div className="credit-icon">
          <Gem size={48} />
        </div>
        <div className="credit-info">
          <div className="credit-number">{creditStatus.total_credits}</div>
          <div className="credit-label">Total Credits Available</div>
        </div>
      </div>

      {/* Credit Breakdown */}
      <div className="credit-breakdown">
        <div className="credit-item">
          <div className="credit-item-icon">
            <TrendingUp size={20} />
          </div>
          <div className="credit-item-info">
            <span className="credit-item-label">Monthly Credits</span>
            <span className="credit-item-value">{creditStatus.credits_remaining}</span>
          </div>
        </div>

        {creditStatus.one_time_credits > 0 && (
          <div className="credit-item">
            <div className="credit-item-icon">
              <Gem size={20} />
            </div>
            <div className="credit-item-info">
              <span className="credit-item-label">One-time Credits</span>
              <span className="credit-item-value">{creditStatus.one_time_credits}</span>
            </div>
          </div>
        )}
      </div>

      {/* Reset Info */}
      {creditStatus.credits_reset_date && (
        <div className="credit-reset">
          <Calendar size={16} />
          <span>{getResetMessage()}</span>
        </div>
      )}

      {/* Warning or CTA */}
      {!creditStatus.can_generate_video ? (
        <div className="credit-warning">
          <p>⚠️ You're out of credits!</p>
          <button onClick={onUpgrade} className="btn-upgrade">
            Upgrade Plan or Buy Credits
          </button>
        </div>
      ) : creditStatus.total_credits <= 2 && (
        <div className="credit-warning low">
          <p>⚡ Running low on credits</p>
          <button onClick={onUpgrade} className="btn-upgrade-secondary">
            Get More Credits
          </button>
        </div>
      )}

      {/* Credit Info */}
      <div className="credit-help">
        <p className="credit-help-text">
          💡 <strong>1 credit = 30 seconds</strong> of video
        </p>
        <ul className="credit-examples">
          <li>30s video = 1 credit</li>
          <li>60s video = 2 credits</li>
          <li>90s video = 3 credits</li>
        </ul>
      </div>
    </div>
  );
});

CreditStatus.displayName = 'CreditStatus';

export default CreditStatus;
